import type { Arrow, GridPoint, Level, AnimatingArrow } from './types';
import { t } from './i18n';

const DOT_RADIUS     = 1;
const ARROW_WIDTH_RATIO = 0.19;  // lineWidth = cellSize * ratio ≈ 3.5 at default cellSize ~18
const HEAD_LEN_RATIO = 0.65;  // relative to cellSize
const HEAD_W_RATIO   = 0.40;

// ── Theme (CSS variables, read once) ────────────────────────────────────────

interface Theme {
  bg: string;
  dot: string;
  arrow: string;
  error: string;
  wonText: string;
  textDim: string;
  overlayBgWon: string;
  overlayBgLost: string;
  confetti: string[];
}

function readTheme(): Theme {
  const cs = getComputedStyle(document.documentElement);
  const v = (n: string) => cs.getPropertyValue(n).trim();
  const confetti: string[] = [];
  for (let i = 1; i <= 6; i++) confetti.push(v(`--confetti-${i}`));
  return {
    bg: v('--bg'), dot: v('--dot'), arrow: v('--arrow'), error: v('--error'),
    wonText: v('--won-text'), textDim: v('--text-dim'),
    overlayBgWon: v('--overlay-bg-won'), overlayBgLost: v('--overlay-bg-lost'),
    confetti,
  };
}

const theme = readTheme();

export interface RenderLayout {
  cellSize: number;
  originX: number;
  originY: number;
}

export function computeLayout(
  viewW: number,
  viewH: number,
  cols: number,
  rows: number
): RenderLayout {
  const padding = 0.86;
  const fitCell = Math.floor(Math.min(viewW, viewH) * padding / Math.max(cols - 1, rows - 1));
  const maxCell = Math.round(Math.min(viewW, viewH) * 0.048);
  const cellSize = Math.min(fitCell, maxCell);
  const originX = (viewW - cellSize * (cols - 1)) / 2;
  const originY = (viewH - cellSize * (rows - 1)) / 2;
  return { cellSize, originX, originY };
}

export function gridToScreen(
  col: number,
  row: number,
  layout: RenderLayout
): { x: number; y: number } {
  return {
    x: layout.originX + col * layout.cellSize,
    y: layout.originY + row * layout.cellSize,
  };
}

// ── Easing ────────────────────────────────────────────────────────────────────

function easeOutCubic(t: number): number { return 1 - Math.pow(1 - t, 3); }

// ── Static arrow drawing ──────────────────────────────────────────────────────

function drawStaticArrow(
  ctx: CanvasRenderingContext2D,
  arrow: Arrow,
  layout: RenderLayout,
  isErrored: boolean
) {
  if (arrow.points.length < 2) return;
  const pts = arrow.points.map(p => gridToScreen(p.col, p.row, layout));
  const color = isErrored ? theme.error : theme.arrow;

  ctx.strokeStyle = color;
  ctx.lineWidth   = layout.cellSize * ARROW_WIDTH_RATIO;
  ctx.lineCap     = 'round';
  ctx.lineJoin    = 'round';
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
  ctx.stroke();

  drawHead(ctx, pts[pts.length - 1], pts[pts.length - 2], color, layout.cellSize);
}

function drawHead(
  ctx: CanvasRenderingContext2D,
  tip: { x: number; y: number },
  prev: { x: number; y: number },
  color: string,
  cellSize: number
) {
  const headLen = cellSize * HEAD_LEN_RATIO;
  const headW   = cellSize * HEAD_W_RATIO;
  const angle = Math.atan2(tip.y - prev.y, tip.x - prev.x);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(tip.x + Math.cos(angle) * headLen,
             tip.y + Math.sin(angle) * headLen);
  ctx.lineTo(tip.x + Math.cos(angle + Math.PI * 0.75) * headW * 1.2,
             tip.y + Math.sin(angle + Math.PI * 0.75) * headW * 1.2);
  ctx.lineTo(tip.x + Math.cos(angle - Math.PI * 0.75) * headW * 1.2,
             tip.y + Math.sin(angle - Math.PI * 0.75) * headW * 1.2);
  ctx.closePath();
  ctx.fill();
}

// ── Snake (sliding-window) drawing ────────────────────────────────────────────

/**
 * Interpolate a sub-cell position along an array of grid cells.
 * t=0 → cell[0], t=1 → cell[1], t=1.7 → 70% between cell[1] and cell[2], etc.
 */
function lerpOnPath(
  path: GridPoint[],
  t: number,
  layout: RenderLayout
): { x: number; y: number } {
  const i    = Math.max(0, Math.min(path.length - 2, Math.floor(t)));
  const frac = t - Math.floor(t);
  const a    = gridToScreen(path[i].col,     path[i].row,     layout);
  const b    = gridToScreen(path[i + 1].col, path[i + 1].row, layout);
  return { x: a.x + (b.x - a.x) * frac, y: a.y + (b.y - a.y) * frac };
}

/**
 * Draw the arrow as a sliding window [tailProgress, headProgress] along expandedPath.
 * This is what makes bent arrows "pull" their body along the existing path.
 */
function drawSnakeArrow(
  ctx: CanvasRenderingContext2D,
  path: GridPoint[],
  arrowLength: number,
  progress: number,       // cells advanced from original position
  layout: RenderLayout,
  alpha: number,
  isErrored: boolean
) {
  const tailProg = progress;
  const headProg = progress + arrowLength - 1;

  if (headProg < 0 || tailProg >= path.length - 1) return;

  // Build screen-space polyline for the current window
  const screenPts: Array<{ x: number; y: number }> = [];

  // 1. Tail (sub-cell interpolated)
  screenPts.push(lerpOnPath(path, tailProg, layout));

  // 2. All integer nodes strictly inside the window
  const fromI = Math.floor(tailProg) + 1;
  const toI   = Math.min(Math.floor(headProg), path.length - 1);
  for (let i = fromI; i <= toI; i++) {
    screenPts.push(gridToScreen(path[i].col, path[i].row, layout));
  }

  // 3. Head (sub-cell interpolated) — add only if distinct from last point
  const headPt  = lerpOnPath(path, headProg, layout);
  const lastPt  = screenPts[screenPts.length - 1];
  if (Math.abs(headPt.x - lastPt.x) > 0.1 || Math.abs(headPt.y - lastPt.y) > 0.1) {
    screenPts.push(headPt);
  }

  if (screenPts.length < 2) return;

  const color = isErrored ? theme.error : theme.arrow;

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth   = layout.cellSize * ARROW_WIDTH_RATIO;
  ctx.lineCap     = 'round';
  ctx.lineJoin    = 'round';
  ctx.beginPath();
  ctx.moveTo(screenPts[0].x, screenPts[0].y);
  for (let i = 1; i < screenPts.length; i++) ctx.lineTo(screenPts[i].x, screenPts[i].y);
  ctx.stroke();

  drawHead(ctx, screenPts[screenPts.length - 1], screenPts[screenPts.length - 2], color, layout.cellSize);
  ctx.restore();
}

// ── Dots ─────────────────────────────────────────────────────────────────────

// Cache the dot grid as a single Path2D — rebuilt only when the geometry
// (level size / cell / origin) actually changes, instead of on every frame.
let dotsPath: Path2D | null = null;
let dotsKey = '';

function buildDotsPath(level: Level, layout: RenderLayout): Path2D {
  const path = new Path2D();
  for (let r = 0; r < level.rows; r++) {
    for (let c = 0; c < level.cols; c++) {
      const { x, y } = gridToScreen(c, r, layout);
      path.moveTo(x + DOT_RADIUS, y);
      path.arc(x, y, DOT_RADIUS, 0, Math.PI * 2);
    }
  }
  return path;
}

function drawDots(ctx: CanvasRenderingContext2D, level: Level, layout: RenderLayout) {
  const key = `${level.cols}x${level.rows}|${layout.cellSize}|${layout.originX}|${layout.originY}`;
  if (key !== dotsKey) {
    dotsPath = buildDotsPath(level, layout);
    dotsKey = key;
  }
  ctx.fillStyle = theme.dot;
  ctx.fill(dotsPath!);
}

// ── Main render ───────────────────────────────────────────────────────────────

export function renderFrame(
  ctx: CanvasRenderingContext2D,
  viewW: number,
  viewH: number,
  level: Level,
  arrows: Arrow[],
  layout: RenderLayout,
  anim: AnimatingArrow | null,
  now: number,
  erroredArrows: string[]
) {
  ctx.clearRect(0, 0, viewW, viewH);
  ctx.fillStyle = theme.bg;
  ctx.fillRect(0, 0, viewW, viewH);

  drawDots(ctx, level, layout);

  for (const arrow of arrows) {
    const isErrored = erroredArrows.includes(arrow.id);

    if (anim && anim.arrow.id === arrow.id) {
      const elapsed = now - anim.startTime;
      const t       = Math.min(elapsed / anim.duration, 1);

      let progress: number;
      if (anim.type === 'exit') {
        // Linear speed — no deceleration
        progress = t * anim.maxProgress;
      } else {
        // Asymmetric bounce: quick forward (35%) then slow return (65%)
        const FORWARD = 0.35;
        progress = t < FORWARD
          ? easeOutCubic(t / FORWARD) * anim.maxProgress
          : (1 - easeOutCubic((t - FORWARD) / (1 - FORWARD))) * anim.maxProgress;
      }

      const alpha = anim.type === 'exit' ? 1 - easeOutCubic(t) * 0.25 : 1;
      drawSnakeArrow(ctx, anim.expandedPath, anim.arrowLength, progress, layout, alpha, isErrored);
    } else {
      ctx.save();
      drawStaticArrow(ctx, arrow, layout, isErrored);
      ctx.restore();
    }
  }
}

// ── Confetti ──────────────────────────────────────────────────────────────────

interface ConfettiParticle {
  x: number; y: number;
  vx: number; vy: number;
  color: string;
  angle: number; va: number;
  w: number; h: number;
}

let confettiParticles: ConfettiParticle[] = [];
let confettiSpawned = false;

export function resetConfetti() {
  confettiParticles = [];
  confettiSpawned = false;
}

function spawnConfetti(viewW: number, viewH: number) {
  confettiParticles = [];
  for (let i = 0; i < 90; i++) {
    confettiParticles.push({
      x:     Math.random() * viewW,
      y:     Math.random() * viewH * 0.4 - viewH * 0.1,
      vx:    (Math.random() - 0.5) * 3,
      vy:    Math.random() * 2 + 1,
      color: theme.confetti[Math.floor(Math.random() * theme.confetti.length)],
      angle: Math.random() * Math.PI * 2,
      va:    (Math.random() - 0.5) * 0.2,
      w:     Math.random() * 6 + 4,
      h:     Math.random() * 3 + 2,
    });
  }
}

function updateAndDrawConfetti(ctx: CanvasRenderingContext2D, viewH: number) {
  for (let i = confettiParticles.length - 1; i >= 0; i--) {
    const p = confettiParticles[i];
    p.vy    += 0.12;
    p.x     += p.vx;
    p.y     += p.vy;
    p.angle += p.va;
    if (p.y > viewH + 20) {
      confettiParticles.splice(i, 1);
      continue;
    }
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.angle);
    ctx.fillStyle = p.color;
    ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
    ctx.restore();
  }
}

// ── Overlay ───────────────────────────────────────────────────────────────────

export function renderOverlay(
  ctx: CanvasRenderingContext2D,
  viewW: number,
  viewH: number,
  status: 'won' | 'lost' | 'playing',
  lives: number
) {
  const heartSize = 20;
  const margin    = 16;
  for (let i = 0; i < 3; i++) {
    ctx.font = `${heartSize}px sans-serif`;
    ctx.fillText(i < lives ? '♥' : '♡', margin + i * (heartSize + 6), margin + heartSize);
  }

  if (status === 'won') {
    if (!confettiSpawned) { spawnConfetti(viewW, viewH); confettiSpawned = true; }
    ctx.fillStyle = theme.overlayBgWon;
    ctx.fillRect(0, 0, viewW, viewH);
    updateAndDrawConfetti(ctx, viewH);
    ctx.fillStyle    = theme.wonText;
    ctx.font         = 'bold 36px system-ui, sans-serif';
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(t('congrats'), viewW / 2, viewH / 2 - 20);
    ctx.fillStyle = theme.textDim;
    ctx.font      = '18px system-ui, sans-serif';
    ctx.fillText(t('tap_to_continue'), viewW / 2, viewH / 2 + 24);
    ctx.textAlign    = 'left';
    ctx.textBaseline = 'alphabetic';
  }

  if (status === 'lost') drawModal(ctx, viewW, viewH, t('try_again'), theme.error);
}

function drawModal(
  ctx: CanvasRenderingContext2D,
  viewW: number, viewH: number,
  text: string, color: string
) {
  ctx.fillStyle = theme.overlayBgLost;
  ctx.fillRect(0, 0, viewW, viewH);
  ctx.fillStyle    = color;
  ctx.font         = 'bold 32px system-ui, sans-serif';
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, viewW / 2, viewH / 2 - 20);
  ctx.fillStyle = theme.textDim;
  ctx.font      = '18px system-ui, sans-serif';
  ctx.fillText(t('tap_to_continue'), viewW / 2, viewH / 2 + 24);
  ctx.textAlign    = 'left';
  ctx.textBaseline = 'alphabetic';
}
